import { api, edenFetch } from "@/lib/eden";

/**
 * Shared plumbing for GET /user/me (the member's own profile + self-contact
 * dates). One query key so every consumer (account settings, /infographic
 * prefill, …) shares a single cache entry and save invalidations reach all of
 * them — two ad-hoc keys for this endpoint caused a stale-prefill bug.
 *
 * The endpoint is `t.Any()` server-side; consumers narrow via `MeProfile` (the
 * shared slice) or their own wider cast over the same fetcher.
 */
export const ME_QUERY_KEY = ["account", "me"] as const;

/** A self-contact date as /user/me returns it (PRD 08 D4). */
export interface MeSelfDate {
  reminder_id: string;
  event_month: number | null;
  event_day: number | null;
  event_year: number | null;
}

/** The commonly-read slice of the /user/me payload. */
export interface MeProfile {
  display_name: string | null;
  /** IANA zone scheduled sends and reminders are anchored to (spec 14 §103). */
  timezone: string | null;
  birthday: MeSelfDate | null;
  anniversary: MeSelfDate | null;
}

export const fetchMe = () => edenFetch(api.api.user.me.get()) as Promise<MeProfile>;

/** Which federated identities are linked (PRD 08 D7). */
export interface MeProviders {
  google: boolean;
  apple: boolean;
  facebook: boolean;
}

/**
 * The wider account-management slice of /user/me (same endpoint + query key as
 * `fetchMe`, so a single cache entry serves both and one invalidation reaches
 * every consumer). Used by the account surfaces that manage identities and the
 * deletion window.
 */
export interface MeAccount extends MeProfile {
  /** Non-null = a D5 deletion window is running; the client offers cancel. */
  scheduled_deletion_at: string | null;
  /** Has a password set — gates whether the last provider can be disconnected. */
  has_password: boolean;
  providers: MeProviders;
}

export const fetchMeAccount = () => edenFetch(api.api.user.me.get()) as Promise<MeAccount>;
